/**
 * User API Service
 * 使用者管理 API 服務
 */
import apiClient from '../client';

export interface User {
  id: number;
  organization_id: number;
  username: string;
  email: string;
  full_name: string;
  phone?: string;
  department?: string;
  job_title?: string;
  role: 'system_admin' | 'org_admin' | 'project_manager' | 'data_entry' | 'viewer';
  status: 'active' | 'inactive' | 'locked';
  last_login_at?: string;
  last_login_ip?: string;
  created_at: string;
  updated_at: string;
}

export interface CreateUserRequest {
  organization_id: number;
  username: string;
  email: string;
  password: string;
  full_name: string;
  phone?: string;
  department?: string;
  job_title?: string;
  role: 'system_admin' | 'org_admin' | 'project_manager' | 'data_entry' | 'viewer';
}

export interface UpdateUserRequest {
  username?: string;
  email?: string;
  full_name?: string;
  phone?: string;
  department?: string;
  job_title?: string;
  role?: 'system_admin' | 'org_admin' | 'pm' | 'member';
  status?: 'active' | 'inactive' | 'locked';
}

export interface UserListResponse {
  total: number;
  items: User[];
}

export interface UpdatePasswordRequest {
  new_password: string;
  confirm_password: string;
}

export interface ChangePasswordRequest {
  new_password: string;
  confirm_password: string;
}

class UserService {
  /**
   * 獲取使用者列表
   */
  async getUsers(params?: {
    skip?: number;
    limit?: number;
    organization_id?: number;
    role?: string;
    status?: string;
  }): Promise<UserListResponse> {
    const response = await apiClient.get<UserListResponse>(
      '/api/v1/users',
      { params }
    );
    return response.data;
  }

  /**
   * 獲取單個使用者
   */
  async getUser(id: number): Promise<User> {
    const response = await apiClient.get<User>(
      `/api/v1/users/${id}`
    );
    return response.data;
  }

  /**
   * 創建使用者 (system_admin, org_admin)
   */
  async createUser(data: CreateUserRequest): Promise<User> {
    const response = await apiClient.post<User>(
      '/api/v1/users',
      data
    );
    return response.data;
  }

  /**
   * 更新使用者
   */
  async updateUser(
    id: number,
    data: UpdateUserRequest
  ): Promise<User> {
    const response = await apiClient.put<User>(
      `/api/v1/users/${id}`,
      data
    );
    return response.data;
  }

  /**
   * 刪除使用者 (system_admin, org_admin)
   */
  async deleteUser(id: number): Promise<void> {
    await apiClient.delete(`/api/v1/users/${id}`);
  }

  /**
   * 重設使用者密碼 (管理員功能)
   */
  async resetUserPassword(
    id: number,
    data: UpdatePasswordRequest
  ): Promise<void> {
    await apiClient.post(`/api/v1/users/${id}/password`, data);
  }

  /**
   * 更新使用者狀態
   */
  async updateUserStatus(
    id: number,
    status: string
  ): Promise<User> {
    const response = await apiClient.put<User>(
      `/api/v1/users/${id}/status`,
      { status_value: status }
    );
    return response.data;
  }

  /**
   * 修改自己的密碼 (需要舊密碼)
   */
  async changeMyPassword(data: ChangePasswordRequest): Promise<void> {
    await apiClient.post('/api/v1/users/me/password', data);
  }
}

export default new UserService();
